import {
  IPayoutDetails,
  IAccountsProduct,
  IPolicy,
  IProductDetail,
  ProductDetailSlug,
} from "config/types";
import { getVerifiedInitialData } from "store/actions/appLoad";
import { logout } from "store/actions/authentication";
import { getDashboardData } from "store/actions/dashboard";
import { getCurrentUserPastDueInvoices } from "store/actions/invoice";
import { getCurrentUserPolicy, getPolicyDocuments } from "store/actions/policy";
import { toFixed } from "utils/toFixed";

import { createReducer } from "@reduxjs/toolkit";

export interface IPolicyState extends IPolicy {
  accountsProduct: IAccountsProduct;
  label: string;
  payoutDetails: IPayoutDetails;
  productDetail: Omit<IProductDetail, "slug"> & { slug: ProductDetailSlug | "" };
}

const defaultState: IPolicyState = {
  id: "",
  label: "",
  status: "",
  amount: 0,
  termDuration: 0,
  termUnit: "month",
  increaseCoverageAmountAllowed: false,
  insuredsAgeAtTerm: 0,
  premiums: [],
  product: undefined,
  effectiveAt: "",
  pausedAt: "",
  terminatesAt: "",
  lapsesAt: "",
  effectiveUntil: "",
  documents: [],
  accountsProduct: {
    id: "",
    amount: 0,
    billingFrequency: "monthly",
    nextPayment: {
      date: "",
      estimated: false,
      invoiceId: "",
    },
  },
  classification: {
    id: "",
    name: "",
    gender: "",
  },
  productDetail: {
    id: "",
    name: "",
    slug: "",
    status: "",
    description: "",
  },
  payoutDetails: {
    autoPedestrian: 0,
    generalAccident: 0,
    publicTransportation: 0,
  },
};

const policy = createReducer(defaultState, builder => {
  builder.addCase(getDashboardData.fulfilled, (state, action) => {
    const accountLabel = action.payload?.label || "";
    const policyPayload = (action.payload?.activePolicy || {}) as Partial<IPolicyState>;
    const accountsProductPayload = policyPayload?.accountsProduct || state.accountsProduct;
    const accountsProductAmount = parseFloat(
      toFixed(accountsProductPayload?.amount || state.accountsProduct.amount)
    );
    const accountsProduct = {
      ...state.accountsProduct,
      ...accountsProductPayload,
      amount: accountsProductAmount,
    };
    const productDetail = policyPayload?.product || state.productDetail;
    const payoutDetails = policyPayload?.payoutDetails || state.payoutDetails;

    Object.assign(state, policyPayload, {
      accountsProduct,
      label: accountLabel,
      productDetail,
      payoutDetails,
    });
  });
  builder.addCase(getCurrentUserPolicy.fulfilled, (state, action) => {
    const policyPayload = (action.payload.activePolicy || {}) as Partial<IPolicyState>;
    const defaultAccountsProduct = policyPayload?.accountsProduct || state.accountsProduct;
    const accountsProductAmount = parseFloat(toFixed(defaultAccountsProduct?.amount || 0));
    const accountsProduct = { ...defaultAccountsProduct, amount: accountsProductAmount };
    const productDetail = policyPayload?.product || state.productDetail;
    const payoutDetails = policyPayload?.payoutDetails || state.payoutDetails;

    Object.assign(state, policyPayload, { accountsProduct, productDetail, payoutDetails });
  });
  builder.addCase(getCurrentUserPastDueInvoices.fulfilled, (state, action) => {
    const policyPayload = (action.payload.activePolicy || {}) as Partial<IPolicyState>;

    state.lapsesAt = policyPayload.lapsesAt ?? state.lapsesAt;
  });
  builder.addCase(getVerifiedInitialData.fulfilled, (state, action) => {
    const account = action.payload?.account;
    const accountLabel = account?.label || "";
    const policyPayload = (account?.activePolicy || {}) as Partial<IPolicyState>;
    const defaultAccountsProduct = policyPayload?.accountsProduct || state.accountsProduct;
    const accountsProductAmount = parseFloat(toFixed(defaultAccountsProduct?.amount || 0));
    const accountsProduct = { ...defaultAccountsProduct, amount: accountsProductAmount };
    const productDetail = policyPayload?.product || state.productDetail;
    const payoutDetails = policyPayload?.payoutDetails || state.payoutDetails;

    Object.assign(state, policyPayload, {
      accountsProduct,
      label: accountLabel,
      productDetail,
      payoutDetails,
    });
  });
  builder.addCase(getPolicyDocuments.fulfilled, (state, action) => {
    state.documents = action.payload;
  });
  builder.addCase(logout, () => defaultState);
});

export default policy;
